import {APP_INITIALIZER, inject, NgModule} from '@angular/core';
import {BrowserModule, REMOVE_STYLES_ON_COMPONENT_DESTROY} from '@angular/platform-browser';

import {AppComponent} from './app.component';
import {TopToolbarComponent} from './nav/top-toolbar.component';
import {MatMenuModule} from '@angular/material/menu';
import {MatIconModule} from '@angular/material/icon';
import {MatButtonModule} from '@angular/material/button';
import {ActivatedRouteSnapshot, CanActivateFn, CanDeactivateFn, Router, RouterModule, RouterStateSnapshot} from '@angular/router';
import type {Observable} from 'rxjs';
import {HTTP_INTERCEPTORS, HttpClient, HttpClientModule} from '@angular/common/http';
import {MatDialogModule} from '@angular/material/dialog';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {MatBottomSheetModule} from '@angular/material/bottom-sheet';
import {MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltipModule} from '@angular/material/tooltip';
import {TOOLTIP_BASE_OPTIONS} from './shared/helpers/tooltip-defaults';
import {BlockUIModule} from 'ng-block-ui';
import {AuthTokenInterceptor} from './interceptors/auth.interceptor';
import {BlockUiInterceptor} from './interceptors/block-ui.interceptor';
import {MatDividerModule} from '@angular/material/divider';
import {MapContextInterceptor} from './shared/interceptors/map-context.interceptor';
import {MatInputModule} from '@angular/material/input';
import {MatChipsModule} from '@angular/material/chips';
import {MatAutocompleteModule} from '@angular/material/autocomplete';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {MatExpansionModule} from '@angular/material/expansion';
import {MatToolbarModule} from '@angular/material/toolbar';
import {PackageService} from './api/services/package.service';
import {HasPackageDirective} from './packages/has-package.directive';
import {HasModuleDirective} from './packages/has-module.directive';
import {MarkdownModule, MARKED_OPTIONS} from 'ngx-markdown';
import {MatProgressBarModule} from '@angular/material/progress-bar';
import {MatSnackBarModule} from '@angular/material/snack-bar';
import {TextInputComponent} from './shared/forms/text-input/text-input.component';
import {EditModeButtonComponent} from './shared/edit-mode-btn/edit-mode-button.component';
import {AppWideStateService} from './services/app-state/app-wide-state.service';
import {isInIframe} from './shared/helpers/maporium.validators';
import {TourMatMenuModule} from 'ngx-ui-tour-md-menu';
import {TranslateHttpLoader} from '@ngx-translate/http-loader';
import {TranslateLoader, TranslateModule} from '@ngx-translate/core';
import {MapService} from './api/services/map.service';
import {UserService} from './api/services/user.service';
import {map, take} from 'rxjs';
import {HasMapPermissionDirective} from './packages/has-map-permission.directive';
import {UserMenuComponent} from './shared/components/user-menu/user-menu';
import {AiGenerationBubbleComponent} from './shared/components/ai-generation-bubble/ai-generation-bubble.component';
import {NotificationContainerComponent} from './shared/components/notification/notification-container.component';

// A polyfill for the passive event listener, which is required for the drag and drop to work
// eslint-disable-next-line @typescript-eslint/no-empty-function
document.addEventListener('mousemove', () => {
}, { passive: false, capture: true });

const leaveDraftGuard: CanDeactivateFn<{canLeave?: () => Observable<boolean> | boolean}> = (component, _route, _state, next) =>
  next.url.startsWith('/signin') || (component?.canLeave?.() ?? true);

const locale = localStorage.getItem('localization') || 'en';

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, 'assets/i18n/', '.json');
}

const currentMapResolver = (route: ActivatedRouteSnapshot,
                            state: RouterStateSnapshot) => {
  const id = route.paramMap.get('id');
  const mapService = inject(MapService);
  return id ? mapService.findByIdWithRelation(id) : null;
};

const dashboardRedirectGuard: CanActivateFn = (route) => {
  const router = inject(Router);
  const mapService = inject(MapService);
  const userService = inject(UserService);

  // Iframe/smaplets mount /dashboard?mapId=… and must not be redirected:
  // the DashboardComponent's own init reads that query param and loads the
  // public map. Redirecting here would strip the param and break the iframe.
  if (isInIframe()) return true;

  // MAP-1497: a shared-smap email link lands on /dashboard?mapId=<id>. Honor
  // that deep-link target so the shared smap opens, instead of falling through
  // to the last-opened/most-recent map (which stripped the param).
  const targetMapId = route.queryParamMap.get('mapId');
  if (targetMapId) {
    return router.createUrlTree(['/dashboard/map', targetMapId]);
  }

  const storedMap = mapService.getCurrentSelectedMapFromStore();
  if (storedMap?.id) {
    return router.createUrlTree(['/dashboard/map', storedMap.id]);
  }

  // MAP-1245: no last-opened smap cached (first sign-in after cloneStarterForUser,
  // or a returning user whose browser cache was cleared). Pick the user's most
  // recent owned map — for a brand-new account this is the admin-configured
  // starter clone. Falls through to the "create/open a smap" prompt only if
  // the user truly has no maps.
  const user = userService.getCurrentUserFromStorage();
  if (!user?.id) return true;

  return mapService.findAllForUser(user.id).pipe(
    take(1),
    map((maps) => {
      const owned = (maps || []).filter(m => m?.owner?.id === user.id);
      if (!owned.length) return true;
      const pick = [...owned].sort((a, b) => {
        const bTime = new Date(b.updatedAt || b.createdAt || 0).getTime();
        const aTime = new Date(a.updatedAt || a.createdAt || 0).getTime();
        return bTime - aTime;
      })[0];
      return router.createUrlTree(['/dashboard/map', pick.id]);
    })
  );
};

const paymentRequiredGuard: CanActivateFn = () => {
  if (isInIframe()) return true;

  const router = inject(Router);
  const packageService = inject(PackageService);
  const userService = inject(UserService);

  return packageService.userPackageStream().pipe(
      take(1),
      map(sub => {
        if (!sub) return true;

        const currentUser = userService.getCurrentUserFromStorage();

        const isTrialWithDeferredBilling = sub.isTrial
            && sub.expirationDate
            && new Date(sub.expirationDate) > new Date()
            && (currentUser?.preferredPackage || sub.package)?.billingPageDisplay === 'after_trial';

        const packageIsFree = sub.package
            && !sub.package.priceMonthly
            && !sub.package.priceYearly;

        // Manually-granted (admin) subscriptions have no Stripe subscription but
        // are still valid while their expiration date is in the future. These are
        // legitimate comped/grandfathered users — don't push them to /payment.
        // Scoped to non-trial so before-trial card collection (MAP-1359) is kept.
        const isManuallyGrantedActive = !sub.isTrial
            && sub.expirationDate
            && new Date(sub.expirationDate) > new Date();

        if (!sub.stripeSubscriptionId && !isTrialWithDeferredBilling && !packageIsFree && !isManuallyGrantedActive) {
          return router.createUrlTree(['/payment']);
        }

        return true;
      })
  );
};

@NgModule({
  declarations: [
    AppComponent,
    TopToolbarComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    BlockUIModule.forRoot(),
    MatMenuModule,
    MatIconModule,
    MatButtonModule,
    HttpClientModule,
    HasPackageDirective,
    HasModuleDirective,
    MatDialogModule,
    MatBottomSheetModule,
    RouterModule.forRoot([
      { path: '', pathMatch: 'full', redirectTo: 'signin' },
      // { path: 'playground', component: CanvasPlaygroundComponent },
      { path: 'signin', loadComponent: () => import('./login/login.component').then(m => m.LoginComponent) },
      { path: 'signup', loadComponent: () => import('./signup/signup.component').then(m => m.SignupComponent) },
      {
        path: 'dashboard/map/:id',
        loadComponent: () => import('./dashboard/dashboard.component').then(m => m.DashboardComponent),
        canActivate: [paymentRequiredGuard],
        resolve: {
          currentMap: currentMapResolver
        }
      },
      {
        path: 'dashboard',
        loadComponent: () => import('./dashboard/dashboard.component').then(m => m.DashboardComponent),
        canActivate: [paymentRequiredGuard, dashboardRedirectGuard]
      },
      {
        path: 'dashboard/draft/:id',
        loadComponent: () => import('./document-draft/document-draft.component').then(m => m.DocumentDraftRouteComponent),
        canActivate: [paymentRequiredGuard],
        canDeactivate: [leaveDraftGuard]
      },
      {
        path: 'dashboard/map-404',
        loadComponent: () => import('./errors/404.component').then(m => m.NotFoundComponent)
      },
      {
        path: '404',
        loadComponent: () => import('./errors/404.component').then(m => m.NotFoundComponent)
      },
      {
        path: 'inactive',
        loadComponent: () => import('./errors/inactive.component').then(m => m.InactiveComponent)
      },
      {
        path: 'payment',
        loadComponent: () => import('./payment/payment.component').then(m => m.PaymentComponent)
      },
      // Close account flow
      {
        path: 'close-account/:token',
        loadComponent: () => import('./close-account/confirm-close-account.component')
          .then(m => m.ConfirmCloseAccountComponent)
      },
      {
        path: 'close-account',
        loadComponent: () => import('./close-account/request-close-account.component')
          .then(m => m.RequestCloseAccountComponent)
      },
      {
        path: 'deprecated-domain',
        loadComponent: () => import('./deprecated-domain/deprecated-domain.component')
          .then(m => m.DeprecatedDomainComponent)
      }
    ]),
    MarkdownModule.forRoot({
      markedOptions: {
        provide: MARKED_OPTIONS,
        useValue: {
          gfm: false,
          breaks: true
        }
      }
    }),
    MatTooltipModule,
    MatDividerModule,
    MatInputModule,
    MatChipsModule,
    MatSnackBarModule,
    MatAutocompleteModule,
    ReactiveFormsModule,
    FormsModule,
    MatExpansionModule,
    MatToolbarModule,
    MatProgressBarModule,
    TextInputComponent,
    TourMatMenuModule,
    EditModeButtonComponent,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      },
      defaultLanguage: locale
    }),
    HasMapPermissionDirective,
    UserMenuComponent,
    AiGenerationBubbleComponent,
    NotificationContainerComponent
  ],
  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: AuthTokenInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: BlockUiInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: MapContextInterceptor, multi: true },
    {
      provide: REMOVE_STYLES_ON_COMPONENT_DESTROY,
      useValue: false
    },
    // { provide: ErrorHandler, useClass: GlobalErrorHandler },
    {
      provide: APP_INITIALIZER,
      useFactory: (packageService: PackageService) => () => packageService.init(),
      deps: [PackageService],
      multi: true
    },
    {
      provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
      useValue: TOOLTIP_BASE_OPTIONS
    }
  ],
  bootstrap: [AppComponent],
  exports: [
    HasModuleDirective
  ]
})
export class AppModule {
  constructor(private appStateService: AppWideStateService) {
    this.appStateService.setReadMode(isInIframe());
  }
}
